import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, Typography, useMediaQuery } from '@mui/material';
import JSONEditor, { JSONEditorOptions } from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import Tooltip from '../ui/tooltip';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

type AddBankModalProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: (data: { bankName: string; countryId: string; importFormat: string }) => void;
	isEdit?: { id: number, state: boolean };
};

const AddBankModal: React.FC<AddBankModalProps> = ({ open, onClose, onConfirm, isEdit }) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [bankName, setBankName] = useState('');
	const [country, setCountry] = useState('');
	const [importFormat, setImportFormat] = useState('');
	const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
	const [errors, setErrors] = useState<{ bankName?: string; country?: string; importFormat?: string }>({});
	const editorRef = useRef<HTMLDivElement>(null);
	const editorInstanceRef = useRef<JSONEditor | null>(null);

	const [fetchCountries, setFetchCountries] = useApi();
	const [fetchBankDetails, setFetchBankDetails] = useApi();

	useEffect(() => {
		if(!open) return;
		const { url, method } = buildApiRequestConfig(routes.country.fetch);
		setFetchCountries({ url, method });
	}, [open, setFetchCountries]);

	useEffect(() => {
		if (!open) return;
		
		// Small delay to ensure DOM is ready
		const timer = setTimeout(() => {
			if (editorRef.current && !editorInstanceRef.current) {
				const options: JSONEditorOptions = {
					mode: 'code',
					indentation: 2,
					search: false,
					enableSort: false,
					enableTransform: false,
				};
				try {
					let initialValue;
					if (importFormat) {
						initialValue = JSON.parse(importFormat);
					} else if (!isEdit?.state) {
						// Default template for new banks
						initialValue = {
							"header": true,
							"amountType": "separate", // or "single"
							"debitColumn": "Debit",
							"creditColumn": "Credit",
							"dateColumn": "Date",
							"descriptionColumn": "Description",
							"dateformat": "DD/MM/YYYY",
						};
					} else {
						initialValue = {};
					}
					editorInstanceRef.current = new JSONEditor(editorRef.current, options, initialValue);
					// Focus the editor after initialization
					if (editorInstanceRef.current) {
						setTimeout(() => {
							const textarea = editorRef.current?.querySelector('textarea');
							if (textarea) {
								textarea.focus();
							}
						}, 100);
					}
				} catch (e) {
					editorInstanceRef.current = new JSONEditor(editorRef.current, options, {});
				}
			}
		}, 0);
		
		return () => {
			clearTimeout(timer);
			// Cleanup when modal closes
			if (editorInstanceRef.current) {
				editorInstanceRef.current.destroy();
				editorInstanceRef.current = null;
			}
		};
	}, [open, isEdit?.state]);

	useEffect(() => {
		return () => {
			if (editorInstanceRef.current) {
				editorInstanceRef.current.destroy();
				editorInstanceRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const { data, isLoading } = fetchCountries;
		if (data && !isLoading) {
			setCountryOptions(data.map((country: any) => ({
				value: country.id,
				label: country.country
			})));
		}
	}, [fetchCountries]);

	useEffect(() => {
		if(isEdit?.state && isEdit?.id) {
			const { url, method } = buildApiRequestConfig(routes.bank.fetchById, [{ param: 'id', value: isEdit.id }]);
			setFetchBankDetails({ url, method });
		} else {
			setErrors({});
			setBankName('');
			setCountry('');
			setImportFormat('');
		}
	}, [isEdit, setFetchBankDetails]);

	useEffect(() => {
		const { data, isLoading } = fetchBankDetails;
		if (data && !isLoading) {
			setBankName(data.bankName);
			setCountry(data.countryId);
			setImportFormat(data.importFormat);
			// Update editor with fetched data
			if (editorInstanceRef.current) {
				try {
					const parsedFormat = JSON.parse(data.importFormat);
					editorInstanceRef.current.set(parsedFormat);
				} catch (e) {
					editorInstanceRef.current.set({});
				}
			}
		}
	}, [fetchBankDetails]);

	// Removed isValid as it's no longer needed with JSON editor

	const handleConfirm = () => {
		const newErrors: { bankName?: string; country?: string; importFormat?: string } = {};
		if (!bankName) newErrors.bankName = 'Bank name is required';
		if (!country) newErrors.country = 'Country is required';
		
		let jsonFormatString = '';
		if (editorInstanceRef.current) {
			try {
				const editorValue = editorInstanceRef.current.get();
				jsonFormatString = JSON.stringify(editorValue);
				
				// Validate required fields
				if (!jsonFormatString || jsonFormatString === '{}') {
					newErrors.importFormat = 'Import format cannot be empty';
				} else {
					const parsed = JSON.parse(jsonFormatString);
					
					// Check required fields
					if (parsed.header === undefined) {
						newErrors.importFormat = '"header" field is required';
					} else if (parsed.amountType === undefined) {
						newErrors.importFormat = '"amountType" field is required ("separate" or "single")';
					} else if (!['separate', 'single'].includes(parsed.amountType)) {
						newErrors.importFormat = '"amountType" must be "separate" or "single"';
					} else if (parsed.header === true) {
						// When header is true, columns should be strings (column names)
						if (parsed.amountType === 'separate') {
							if (!parsed.debitColumn || typeof parsed.debitColumn !== 'string') {
								newErrors.importFormat = '"debitColumn" must be a string (column name)';
							} else if (!parsed.creditColumn || typeof parsed.creditColumn !== 'string') {
								newErrors.importFormat = '"creditColumn" must be a string (column name)';
							}
						} else if (parsed.amountType === 'single') {
							if (!parsed.amountColumn || typeof parsed.amountColumn !== 'string') {
								newErrors.importFormat = '"amountColumn" must be a string (column name)';
							}
						}
						if (!newErrors.importFormat && (!parsed.dateColumn || typeof parsed.dateColumn !== 'string')) {
							newErrors.importFormat = '"dateColumn" must be a string (column name)';
						} else if (!newErrors.importFormat && (!parsed.descriptionColumn || typeof parsed.descriptionColumn !== 'string')) {
							newErrors.importFormat = '"descriptionColumn" must be a string (column name)';
						}
					} else if (parsed.header === false) {
						// When header is false, columns should be numbers (column indices)
						if (parsed.amountType === 'separate') {
							if (parsed.debitColumn === undefined || typeof parsed.debitColumn !== 'number') {
								newErrors.importFormat = '"debitColumn" must be a number (column index)';
							} else if (parsed.creditColumn === undefined || typeof parsed.creditColumn !== 'number') {
								newErrors.importFormat = '"creditColumn" must be a number (column index)';
							}
						} else if (parsed.amountType === 'single') {
							if (parsed.amountColumn === undefined || typeof parsed.amountColumn !== 'number') {
								newErrors.importFormat = '"amountColumn" must be a number (column index)';
							}
						}
						if (!newErrors.importFormat && (parsed.dateColumn === undefined || typeof parsed.dateColumn !== 'number')) {
							newErrors.importFormat = '"dateColumn" must be a number (column index)';
						} else if (!newErrors.importFormat && (parsed.descriptionColumn === undefined || typeof parsed.descriptionColumn !== 'number')) {
							newErrors.importFormat = '"descriptionColumn" must be a number (column index)';
						}
					}
					
					if (!newErrors.importFormat && (!parsed.dateformat || typeof parsed.dateformat !== 'string')) {
						newErrors.importFormat = '"dateformat" field is required (e.g., "DD/MM/YYYY")';
					}
				}
			} catch (e) {
				newErrors.importFormat = 'Invalid JSON format';
			}
		} else {
			newErrors.importFormat = 'Import format is required';
		}
		
		if (!newErrors.bankName && !newErrors.country && !newErrors.importFormat) {
			onConfirm({ bankName, countryId: country, importFormat: jsonFormatString });
			setBankName('');
			setCountry('');
			setImportFormat('');
			if (editorInstanceRef.current) {
				editorInstanceRef.current.set({});
			}
		} else {
			setErrors(newErrors);
		}
	};

	const content = (
		<>
			<DialogTitle>
				<Typography sx={{ fontWeight: 700, fontSize: 22 }}>{isEdit?.state ? 'Edit Bank' : 'Add New Bank'}</Typography>
			</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={3} mt={1}>
					<Box>
						<Typography fontSize={14} color='var(--color-text-muted)'>
							Bank name <span style={{ color: '#ff5252' }}>*</span>
						</Typography>
						<TextField
							placeholder="Enter bank name"
							value={bankName}
							onChange={e => setBankName(e.target.value)}
							size="small"
							fullWidth
							error={!!errors.bankName}
							helperText={errors.bankName}
							InputProps={{ style: { borderRadius: 8 } }}
							inputProps={{ maxLength: 64 }}
						/>
					</Box>
					<Box>
						<Typography fontSize={14} color='var(--color-text-muted)'>
							Country <span style={{ color: '#ff5252' }}>*</span>
						</Typography>
						<Dropdown
							value={country}
							onChange={setCountry}
							options={countryOptions}
							height={40}
							error={!!errors.country}
							helperText={errors.country}
						/>
					</Box>
					<Box>
                        <Box display="flex" alignItems="center">
                            <Typography fontSize={14} color='var(--color-text-muted)'>
                                Import Format (JSON) <span style={{ color: '#ff5252' }}>*</span>
                            </Typography>
                            <Tooltip 
								message={
									<span style={{ fontStyle: 'italic' }}>
										Define the column structure in JSON format.<br />
										Example: {`{"Date": "DD/MM/YYYY", "Amount": "number"}`}
									</span>
								}
							/>
                        </Box>
						<Box
							sx={{
								border: errors.importFormat ? '1px solid #f44336' : '1px solid #ccc',
								borderRadius: 1,
								overflow: 'hidden',
								position: 'relative',
								zIndex: 1,
								'& .jsoneditor': {
									borderRadius: '8px',
									border: 'none',
								},
								'& .jsoneditor-menu': {
									backgroundColor: 'var(--color-primary)',
									borderBottom: '1px solid #ddd',
									zIndex: 2,
								},
								'& .jsoneditor-modes button.selected': {
									backgroundColor: 'var(--color-primary)',
									color: '#fff',
								},
								'& textarea': {
									fontFamily: 'monospace',
									fontSize: '8px',
									zIndex: 1,
								}
							}}
							onClick={() => {
								const textarea = editorRef.current?.querySelector('textarea');
								if (textarea) textarea.focus();
							}}
						>
							<div
								ref={editorRef}
								style={{
									height: '200px',
									borderRadius: '8px',
									width: '100%',
								}}
							/>
						</Box>
						{errors.importFormat && (
							<Typography sx={{ fontSize: 12, color: '#f44336', mt: 0.5 }}>
								{errors.importFormat}
							</Typography>
						)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
				<Button
					onClick={onClose}
					variant="contained"
					style={{
						background: 'var(--color-container-background)',
						color: 'var(--color-default-text)',
						borderRadius: 8,
						fontWeight: 600,
						boxShadow: 'none',
						textTransform: 'none'
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					style={{
						background: 'var(--color-primary)',
						color: '#fff',
						borderRadius: 8,
						fontWeight: 600,
						boxShadow: 'none',
						textTransform: 'none'
					}}
				>
					{isEdit?.state ? 'Save Changes' : 'Add Bank'}
				</Button>
			</DialogActions>
		</>
	);

	return isMobile ? (
		<BottomSheetDialog open={open} onClose={onClose} heightVh={90}>
			{content}
		</BottomSheetDialog>
	) : (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 16, background: 'var(--color-page-background)' } }}>
			{content}
		</Dialog>
	);
};

export default AddBankModal;
